import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { EmailAuthProvider } from 'firebase/auth';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { FEATURES_DASHBOARD } from '../interface/features.interface';
import { ROUTES } from '../shared/sidebar/menu-items';
import { DataService } from './data.service';
import { MessagingService } from './messaging.service';
import { WhiteLabelService } from './white-label.service';
import { ModalDatabaseUseAuthorizationComponent } from 'src/app/shared/modals/modal-database-use-authorization/modal-database-use-authorization.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UserRoles, UserSession } from '../interface/users.interface';
import { ApiDetailResponse } from '../interface/data.interface';

export const HEADER_FB_TOKEN = 'fb-token';

@Injectable()
export class AuthService extends DataService {
  messaging: any;
  currentMessage = new BehaviorSubject(null);
  userId = null;
  dialogRefDatabaseUseAuthorization: MatDialogRef<ModalDatabaseUseAuthorizationComponent, any> | null = null;

  constructor(
    private http: HttpClient,
    public afAuth: AngularFireAuth,
    private messagingService: MessagingService,
    protected injector: Injector,
    private router: Router,
    private toastr: ToastrService,
    private translate: TranslateService,
    protected store: Store<any>,
    private matDialog: MatDialog,
    private whiteLabelService: WhiteLabelService,
  ) {
    super(injector, store);
    this.dialogRefDatabaseUseAuthorization = null;
  }

  async doLogin(params) {
    try {
      return new Promise((resolve, reject) => {
        this.afAuth
          .signInWithEmailAndPassword(params.email, params.password)
          .then((res) =>
            res.user.getIdToken().then(async (token) => {
              const fcmToken: any = await this.messagingService.requestPermission();
              this.messagingService.receiveMessage();
              return this.doLoginKaus(params.email, token, fcmToken)
                .then((r: any) => {
                  let user = r.user;
                  let userBuilding = r.newFormat.userBuilding;
                  if (user.superUser && localStorage.getItem('usersSessions')?.length > 0)
                    return reject({ inactive: true, message: 'toastrMessage.error.actionDeniedByRole' });
                  this.userId = user.id;
                  if (user.flag && !user.flag.changePassword) return reject({ notChangePassword: true });

                  const buildings = r.user.buildings;
                  if (buildings.length > 0 && !user.superUser) {
                    if (buildings[0].status !== 'active') return reject({ inactive: true, message: 'toastrMessage.error.buildingInactive' });
                    if (buildings[0].userStatus !== 'active') return reject({ inactive: true, message: 'toastrMessage.error.userInactive' });
                  }

                  user.token = r.token;
                  user.fbToken = token;
                  user.roles = buildings.length > 0 ? (buildings[0].roles ? buildings[0].roles : ['superAdmin']) : ['superAdmin'];
                  user.photoUrl = user.photoUrl;
                  user.isAuth = true;
                  user.features = this.mapFeatures(userBuilding.features);
                  // Backend puede enviar unit poblado sin unit_id en JSON; alinear siempre con la relación userBuilding
                  if (user.buildings?.length && userBuilding) {
                    const ub: any = userBuilding;
                    const fromUnitObj =
                      ub.unit && typeof ub.unit === 'object'
                        ? ub.unit.id ?? ub.unit._id
                        : undefined;
                    const resolvedUnitId =
                      (typeof ub.unit_id === 'string' && ub.unit_id) ||
                      (ub.unit_id && typeof ub.unit_id === 'object' && (ub.unit_id.id ?? ub.unit_id._id)) ||
                      (typeof ub.unit === 'string' && ub.unit) ||
                      fromUnitObj;
                    if (resolvedUnitId) {
                      user.buildings[0].unitId = String(resolvedUnitId);
                    }
                    if (ub.unit && typeof ub.unit === 'object' && ub.unit.number != null) {
                      user.buildings[0].unit = {
                        id: String(ub.unit.id ?? ub.unit._id ?? user.buildings[0].unitId),
                        number: ub.unit.number,
                      };
                    }
                  }
                  this.setUser(user);
                  if (buildings?.length) {
                    this.whiteLabelService.setSessionLoginPathFromBuilding(buildings[0]);
                    this.whiteLabelService.applyFromBuilding(buildings[0]);
                  }
                  const language: string = user.i18n;
                  if (language) this.setLanguage(language.slice(0, 2));
                  return resolve(true);
                })
                .catch((err) => {
                  console.log('-> err: ', err);
                  return reject(err);
                });
            }),
          )
          .catch((err) => {
            console.log(err);
            reject(err);
          });
      });
    } catch (err) {
      console.log('-- err: ', err);
    }
  }

  async doLoginAdonis(email, fbToken, fcmToken) {
    const headers = new HttpHeaders().set('x-platform', 'webpage');
    return this.http
      .post(`${environment.apiUrl}/user/login`, { email, fbToken, fcmToken }, { headers })
      .toPromise()
      .catch((err) => {
        return Promise.reject(err);
      });
  }

  async doLoginKaus(email: string, fbToken: string, fcmToken?: string) {
    const headers = new HttpHeaders().set('x-platform', 'webpage');
    let body: any = {
      email: email,
      fbToken: fbToken,
    };
    if (!!fcmToken) body = { fcmToken: fcmToken, ...body };
    return this.http
      .post(`/api/auth/v1/login`, body, { headers })
      .toPromise()
      .catch((err) => {
        console.log(err);
        return Promise.reject(err);
      });
  }

  doLogoutFirebase() {
    const loginPath = this.prepareLogoutLoginRedirect();
    this.afAuth.signOut().then(() => {
      this.clearUserSession();
      this.router.navigateByUrl(loginPath);
    });
  }

  // Refresca el token de Firebase sin hacer login completo
  async refreshFirebaseToken(): Promise<string | null> {
    try {
      const currentUser = await this.afAuth.currentUser;
      if (currentUser) {
        const token = await currentUser.getIdToken(true);
        const user = this.getUser();
        if (user) {
          user.fbToken = token;
          this.setUser(user);
        }
        return token;
      }
      return null;
    } catch (error) {
      console.error('Error refrescando token de Firebase:', error);
      return null;
    }
  }

  /**
   * Si la sesión activa de Firebase es la misma cuenta que `user` (mismo email),
   * fuerza un ID token fresco y actualiza currentUser y usersSessions.
   */
  async syncFirebaseTokenIfSessionMatches(user: any): Promise<void> {
    try {
      const fbUser = await this.afAuth.currentUser;
      if (!fbUser?.email || !user?.email) return;
      const a = fbUser.email.toLowerCase().trim();
      const b = user.email.toLowerCase().trim();
      if (a !== b) return;
      const token = await fbUser.getIdToken(true);
      user.fbToken = token;
      this.setUser(user);
      this.setUserInMultisesion(user);
    } catch (error) {
      console.error('Error sincronizando token Firebase al activar sesión:', error);
    }
  }

  // Verifica si Firebase tiene una sesión activa
  async hasActiveFirebaseSession(): Promise<boolean> {
    try {
      const currentUser = await this.afAuth.currentUser;
      return currentUser !== null;
    } catch (error) {
      console.error('Error verificando sesión de Firebase:', error);
      return false;
    }
  }

  private clearUserSession(): void {
    this.clearUserCache();
    localStorage.removeItem('currentUser');
    localStorage.removeItem('usersSessions');
  }

  /** Branding y ruta de login post-logout: host dedicado > building > default. */
  private prepareLogoutLoginRedirect(): string {
    const building = this.getBuilding();
    this.whiteLabelService.setSessionLoginPathFromBuilding(building);
    if (!this.whiteLabelService.applyPreLoginForCurrentHost()) {
      this.whiteLabelService.applyFromBuilding(building);
    }
    return this.whiteLabelService.getLoginRedirectUrl();
  }

  doLogoutV2(fcmToken) {
    return this.http.post(`${environment.apiUrl}/auth/v1/logout`, { fcmToken });
  }

  handleLogoutError(err) {
    console.error(err);
    this.toastr.error(this.translate.instant('authentication.error.logout'));
  }

  async doLogout() {
    try {
      const loginPath = this.prepareLogoutLoginRedirect();
      const fcmToken: any = await this.messagingService.requestPermission();
      this.doLogoutV2(fcmToken).subscribe({
        next: () => {
          this.afAuth.signOut().then(() => {
            this.clearUserSession();
            this.router.navigateByUrl(loginPath);
          });
        },
        error: (err) => this.handleLogoutError(err),
      });
    } catch (err) {
      this.handleLogoutError(err);
    }
  }

  async doLogoutAsync() {
    const building = this.getBuilding();
    this.whiteLabelService.setSessionLoginPathFromBuilding(building);
    if (!this.whiteLabelService.applyPreLoginForCurrentHost()) {
      this.whiteLabelService.applyFromBuilding(building);
    }

    const fcmToken: any = await this.messagingService.requestPermission();
    return this.doLogoutV2(fcmToken)
      .toPromise()
      .then(() => {
        this.clearUserSession();
        return this.afAuth.signOut();
      });
  }

  async doLogoutAdonis() {
    const fcmToken = await this.messagingService.requestPermission();
    return this.http
      .post(`${environment.apiUrl}/user/logout`, { fcmToken })
      .toPromise()
      .then((res) => {
        return res;
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  }

  sendPasswordResetEmail(email: string): Promise<string | void> {
    return new Promise((resolve, reject) => {
      const actionCodeSettings = {
        url: `${environment.baseUrl}/#/authentication/login`,
        handleCodeInApp: false,
      };
      this.afAuth
        .sendPasswordResetEmail(email, actionCodeSettings)
        .then((r) => {
          return resolve();
        })
        .catch((err) => {
          return reject(err);
        });
    });
  }

  resetPassword(currentPassword, newPassword) {
    return new Promise((resolve, reject) => {
      this.afAuth.currentUser.then((user: any) => {
        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        user
          .reauthenticateWithCredential(credential)
          .then(() => {
            user
              .updatePassword(newPassword)
              .then(() => {
                this.changeFlagPassword(this.userId, { changePassword: true })
                  .then(() => {
                    resolve(true);
                  })
                  .catch((err) => reject(err));
              })
              .catch((err) => reject(err));
          })
          .catch((err) => reject(err));
      });
    });
  }

  sendResetPassword(currentPassword) {
    return new Promise((resolve, reject) => {
      this.afAuth.currentUser.then((user: any) => {
        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        user
          .reauthenticateWithCredential(credential)
          .then(() => {
            this.sendPasswordResetEmail(user.email)
              .then(() => {
                resolve(true);
              })
              .catch(() => reject(false));
          })
          .catch((err) => reject(err));
      });
    });
  }

  validCurrentPassword(currentPassword) {
    return this.afAuth.currentUser.then((user: any) => {
      const cred = EmailAuthProvider.credential(user.email, currentPassword);
      return user.reauthenticateWithCredential(cred);
    });
  }

  changeFlagPassword(id, params) {
    return this.http
      .put(`${environment.apiUrl}/user/update-change-password/${id}`, params)
      .toPromise()
      .then((res) => {
        return res;
      })
      .catch((err) => {
        console.log(err);
        return Promise.reject(err);
      });
  }

  mapFeatures(featuresList) {
    let features = [];
    if (!featuresList) return features;
    featuresList.forEach((feature) => {
      const featureDashboard = FEATURES_DASHBOARD.find((element) => element.code === feature.feature.code);
      if (featureDashboard) {
        features.push({
          feature: featureDashboard.feature,
          enable: feature.enable,
        });
      }
    });
    return features;
  }

  getUserFirstRoute(user) {
    let route = '/app';
    const feature = user.features.find((feature) => feature.enable === true);
    if (!feature || !feature.feature[0]) return route;
    route = ROUTES[feature.feature[0]].path;
    return route;
  }

  openModalDatabaseUseAuthorization() {
    if (this.dialogRefDatabaseUseAuthorization) return;

    const building = this.getBuilding();
    const user = this.getUser();

    if (!building || !user) return;

    if (!user.roles.includes(UserRoles.admin) || user.email.includes('@getnada') || user.email.includes('@munily')) return;

    if (building && building?.config?.certificationContent?.enable) {
      return;
    }

    this.dialogRefDatabaseUseAuthorization = this.matDialog.open(ModalDatabaseUseAuthorizationComponent, {
      data: { building: { id: building.id, name: building.name } },
      panelClass: 'custom__class-dialog-database-use-authorization',
      disableClose: true,
    });

    this.eventSubcriberCloseModalDatabaseUseAuthorization();
  }

  eventSubcriberCloseModalDatabaseUseAuthorization() {
    this.dialogRefDatabaseUseAuthorization.afterClosed().subscribe((result) => {
      if ((typeof result === 'boolean' && !result) || !result) {
        this.doLogout();
      }
      this.dialogRefDatabaseUseAuthorization = null;
    });
  }

  getUserSession(token: string): Observable<ApiDetailResponse<UserSession>> {
    return this.http.get<ApiDetailResponse<UserSession>>(`${environment.newApiBackend}/api/auth/user-session`, { headers: new HttpHeaders().set(HEADER_FB_TOKEN, token) });
  }

  deleteUserSession() {
    this.clearUserSession();
  }
}
